<template>
  <div class="main">

    <div class="node">
      <div class="title">{{ $t('NODE.title') }}</div>
      <div class="content">
        <div class="left">
          <div>{{ node.count }}</div>
          <div style="font-size: 20px;">{{ $t('node.content') }}</div>
        </div>
        <div class="right">
          <div>{{ node.countryCount }}</div>
          <div style="font-size: 20px;">{{ $t('node.content2') }}</div>
        </div>
      </div>
    </div>

    <div class="distribution">
      <div class="title">{{ $t('NODE.title2') }}</div>
      <div class="content">
        <div class="map" ref="map"></div>
      </div>
    </div>

    <div class="nodesRanking">
      <div class="title">{{ $t('NODE.title3') }}</div>
      <div class="content">
        <div class="dataChart" ref="dataChart"></div>
      </div>
    </div>

  </div>
</template>

<script>

import echarts from 'echarts';
import 'echarts/map/js/world';
import { nodeInfo } from '@/api';

export default {
  name: 'Node',
  components: {  },
  data() {
    return {
      node: {
        count: '',
        countryCount: '',
      },
      distribution: {
        data: [], // 初始化为空数组，强制从接口获取数据
      },
      nodeRank: {
        // 初始化默认数据，确保排名图表始终有内容显示
        xdata: [5, 4, 2, 2, 3],
        ydata: ['美国', '新加坡', '香港', '英国', '德国']
      },
    }
  },
  async mounted() {
      try {
        // 确保DOM渲染完成后再进行操作
        await this.$nextTick();
        console.log('开始加载节点数据...');
        await this.nodeInfo();
      } catch (error) {
        console.error('节点数据加载失败:', error);
        // 即使出错也要初始化图表，确保UI结构完整
        this.initMapChart();
        this.initDataChart();
      }
    },
  methods: {
    async nodeInfo() {
      try {
        console.log('开始获取节点数据...');
        const res = await nodeInfo();
        console.log('API响应:', res);
        
        // 检查响应数据结构是否有效
        if (!res || !res.data) {
          console.error('API返回数据无效或为空，使用默认数据');
          // 不重置数据，保留默认静态数据
          return;
        }
        
        // 安全地设置节点总数和国家/地区数量
        if (res.data.nodeCount) {
          this.node.count = res.data.nodeCount.nodeCount || 0;
          this.node.countryCount = res.data.nodeCount.nodeCountryCount || 0;
        }

        // 标记是否有有效的API数据
        let hasValidMapData = false;
        let hasValidChartData = false;

        // 安全地处理节点分布数据
        if (res.data.nodeMap && Array.isArray(res.data.nodeMap) && res.data.nodeMap.length > 0) {
          const mapData = [];
          res.data.nodeMap.forEach(nodeMap => {
            if (nodeMap && (typeof nodeMap.lat === 'number' || typeof nodeMap.lat === 'string') && 
                (typeof nodeMap.log === 'number' || typeof nodeMap.log === 'string') && nodeMap.count) {
              mapData.push({
                name: nodeMap.name || `节点${mapData.length + 1}`,
                value: [parseFloat(nodeMap.lat), parseFloat(nodeMap.log), nodeMap.count]
              });
              hasValidMapData = true;
            }
          });
          
          if (hasValidMapData) {
            this.distribution.data = mapData;
            console.log('更新后的节点分布数据:', this.distribution.data);
          }
        } else if (res.data.nodeMap && !Array.isArray(res.data.nodeMap)) {
          // 处理可能的非数组格式
          console.warn('节点分布数据格式异常，不是数组:', res.data.nodeMap);
          // 尝试创建一个数组
          const singleData = res.data.nodeMap;
          if (singleData && (typeof singleData.lat === 'number' || typeof singleData.lat === 'string') && 
              (typeof singleData.log === 'number' || typeof singleData.log === 'string') && singleData.count) {
            this.distribution.data = [{
              name: singleData.name || '节点1',
              value: [parseFloat(singleData.lat), parseFloat(singleData.log), singleData.count]
            }];
            hasValidMapData = true;
          }
        }

        // 安全地处理节点排名数据
        if (res.data.nodeChart && Array.isArray(res.data.nodeChart) && res.data.nodeChart.length > 0) {
          const xdata = [];
          const ydata = [];
          
          res.data.nodeChart.forEach(nodeChart => {
            if (nodeChart) {
              xdata.push(nodeChart.count || 0);
              ydata.push(nodeChart.countryName || '未知');
              hasValidChartData = true;
            }
          });
          
          if (hasValidChartData) {
            this.nodeRank.xdata = xdata;
            this.nodeRank.ydata = ydata;
          }
          console.log('处理完成的节点排名数据:', this.nodeRank);
        }
        
        // 如果有新数据，重新初始化图表
        if (hasValidMapData) {
          this.initMapChart();
        }
        if (hasValidChartData) {
          this.initDataChart();
        }
      } catch (error) {
        console.error('获取节点信息失败:', error);
        // 错误时保留默认数据，确保图表仍然有内容显示
      }
    },
    initMapChart() {
      // 检查地图容器是否存在
      if (!this.$refs || !this.$refs.map) {
        console.error('地图容器不存在');
        return;
      }
      
      try {
        const mapChart = echarts.init(this.$refs.map);
        
        // 为确保地图显示，设置背景为透明
        const mapOptions = {
          backgroundColor: 'transparent',
          
          // 图表系列配置
          series: [
            {
              // 散点图类型，用于在地图上显示节点
              type: 'scatter',
              // 使用地理坐标系
              coordinateSystem: 'geo',
              // 数据源，来自组件的数据
              data: this.distribution.data,
              // 标记大小配置函数
              // 修改：调整计算逻辑，使节点显示更大更明显
              symbolSize: function (val) {
                // val[2]是节点数量，调整算法使小数量的节点也有合适大小
                // 将最小标记从10调整到15，最大从30调整到40，除以5而不是10使标记更大
                return Math.max(15, Math.min(40, val[2] / 5));
              },
              // 标签配置
              label: {
                // 正常状态下的标签样式
                normal: {
                  // 标签内容格式化，显示节点数量
                  formatter: function (params) {
                    return params.value[2];
                  },
                  // 是否显示标签
                  show: true,
                  // 文本样式
                  textStyle: {
                    // 修改：将文字颜色从白色改为黑色，提高在亮色背景上的可见性
                    color: 'white',
                    // 修改：增大字号以提高可读性
                    fontSize: 10,
                    // 新增：加粗文字
                    fontWeight: 'bold'
                  }
                },
                // 高亮状态下的标签样式
                emphasis: {
                  textStyle: {
                    color: 'black',
                    // 修改：高亮时字号更大
                    fontSize: 14,
                    fontWeight: 'bold'
                  }
                }
              },
              // 散点标记样式
              itemStyle: {
                normal: {
                  // 修改：将节点颜色从#DE6B67改为更亮的#FF5252（亮红色），提高可见性
                  color: '#FF5252',
                  // 新增：添加阴影效果
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            },
          ],
          // 地理坐标系配置
          geo: {
            // 使用世界地图
            map: 'world',
            // 允许缩放和平移
            roam: true,
            // 设置默认视图中心和缩放级别
            center: [0, 30],
            zoom: 1.2,
            // 地图区域样式
            itemStyle: {
              // 正常状态
              normal: { 
                // 修改：将地图背景色从#364962改为更深的#2C3E50，提高对比度
                areaColor: '#212324af',
                // 修改：将边框颜色从#4c6079改为更亮的#7F8C8D，增加清晰度
                borderColor: '#4c6079',
                borderWidth: 0.5
              },
              // 高亮状态（鼠标悬浮时）
              emphasis: { 
                // 修改：高亮颜色改为#34495E，与正常状态有明显区分
                areaColor: '#34495E',
                // 修改：高亮边框颜色改为#95A5A6，更加突出
                borderColor: '#95A5A6'
              }
            },
            
            // 地图标签配置
            label: {
              // 正常状态
              normal: {
                show: false,
                textStyle: {
                  color: 'white'
                },
              },
              // 高亮状态（鼠标悬浮时）
              emphasis: {
                show: true,
                textStyle: {
                  color: 'white'
                }
              }
            }
          },
          // 添加tooltip以便调试和查看数据
          tooltip: {
            formatter: function(params) {
              return `位置: ${params.name}<br/>节点数: ${params.value[2]}`;
            }
          }
        }

        mapChart.setOption(mapOptions);
        console.log('地图图表初始化完成，数据点数量:', this.distribution.data.length);
        
        // 添加窗口大小变化事件监听，确保图表可以自适应
        window.addEventListener('resize', () => {
          mapChart.resize();
        });
      } catch (error) {
        console.error('地图初始化失败:', error);
        // 即使初始化失败，也确保容器样式正常显示
        this.$refs.map.style.display = 'block';
      }
    },
    initDataChart() {
      // 检查图表容器是否存在
      if (!this.$refs || !this.$refs.dataChart) {
        console.error('数据图表容器不存在');
        return;
      }
      
      try {
        // 设置默认数据，确保图表始终有内容显示
        if (!this.nodeRank.ydata.length) {
          this.nodeRank.ydata = ['中国', '美国', '英国', '新加坡', '日本'];
          this.nodeRank.xdata = [200, 180, 160, 140, 120];
        }
        
        const dataChartDom = this.$refs.dataChart;
        const dataChart = echarts.init(dataChartDom);
        const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          type: 'value',
          position: 'bottom',
          axisLine: {
            lineStyle: {
              color: 'white'
            }
          },
          axisLabel: {
            textStyle: {
              color: 'gray'
            }
          }
        },
        yAxis: {
          type: 'category',
          data: this.nodeRank.ydata,
          axisTick: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: 'gray'
            }
          },
          axisLabel: {
            textStyle: {
              color: 'gray'
            }
          }
        },
        series: [
          {
            type: 'bar',
            data: this.nodeRank.xdata,
            itemStyle: {
              normal: {
                barBorderRadius: [0, 15, 15, 0],
                color: '#DE6B67'
              },
            },
            label: {
              show: true,
              position: 'right',
              formatter: '{c}',
              color: 'black',
              fontWeight: 'bold',
              fontSize: 12
            },
            barWidth: '10px',
            orientation: 'horizontal'
          }
        ]
      };
 
        dataChart.setOption(option);
        console.log('数据图表初始化完成');
        
        // 添加窗口大小变化事件监听
        window.addEventListener('resize', () => {
          dataChart.resize();
        });
      } catch (error) {
        console.error('数据图表初始化失败:', error);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.main {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    /* 固定画布宽度，取消 1200 的最大宽度限制 */
    width: 100%;
   
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;

    /* 高度固定（74vw → ≈ 1421） */
    height: 1421px;
  .node {
    max-width: 1200px;
    width: 100%;
    
    
    .title {
      margin-top: 30px;
      color: #ffffff;
                font-size: 34px;  
                font-weight: 500;
                line-height: 1.2;
                margin-bottom: 30px;
                font-family: 'Arial', sans-serif; 
                
      
      font-weight: bold;
      text-align: left; /* 标题文本居中 */
    }
    .content {
      display: flex;
      margin-top: 10px;
      background-color: white;
      padding: 20px;
      border-radius: 5px;
      .left, .right {
        flex: 1;
        text-align: center;
        div:first-child {
          margin-bottom: 10px;
          font-weight: bold;
        }
      }
    }
  }

  .distribution {
    max-width: 1200px;
    width: 100%;
    
    
    .title {
      margin-top: 34px;
      color: #ffffff;
                font-size: 30px;  
                font-weight: 500;
                line-height: 1.2;
                margin-bottom: 30px;
                font-family: 'Arial', sans-serif; 
                
      
      font-weight: bold;
      text-align: left; /* 标题文本居中 */
    }
    .content {
      margin-top: 10px;
      width: 100%;
      background-color: white;
      padding: 20px;
      border-radius: 5px;
      .map {
        height: 450px;
        border-radius: 5px;
        background: #364962;
        /* 确保容器总是可见，优先级最高 */
        display: block !important;
        position: relative;
        overflow: hidden;
        /* 确保容器在所有情况下都可见 */
        min-height: 450px !important;
        width: 100% !important;
      }
    }
  }

  .nodesRanking {
   max-width: 1200px;
    width: 100%;
    height: 650px;
   
    margin-bottom: 50px; /* 添加底部边距 */
    
    .title {
     margin-top: 30px;
      color: #ffffff;
                font-size: 34px;  
                font-weight: 500;
                line-height: 1.2;
                margin-bottom: 30px;
                font-family: 'Arial', sans-serif; 
                
      
      font-weight: bold;
      text-align: left; /* 标题文本居中 */
    }
    .content {
      margin-top: 10px;
      width: 100%;
      background-color: white;
      border-radius: 5px;
      padding: 20px; /* 添加内边距 */
      .dataChart {
        height: 450px;
        /* 确保图表容器始终可见 */
        min-height: 450px !important;
        width: 100% !important;
        display: block !important;
        position: relative;
        overflow: hidden;
      }
    }
  }
  
}
</style>